// =====================================================
// TOTP MFA Flow Component (SEC task 5)
// Digital Map - Victim Services Mapping Application
// =====================================================
//
// Handles BOTH post-login MFA shapes returned by the backend:
//   - mode 'verify'  (mfaRequired):      6-digit code -> POST /auth/mfa/verify
//   - mode 'setup'   (mfaSetupRequired): fetch enrollment URI, render QR,
//                                        first code -> POST /auth/mfa/activate
// On success the component calls onVerified(user) with the session-bearing
// user object; the login form then completes the auth context state.
// =====================================================

import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { authAPI } from '../../lib/authApi';
import { User } from '../../types/auth';
import { useTranslation } from '../../utils/i18n';

export type MfaMode = 'verify' | 'setup';

interface MfaFlowProps {
  challenge: string;
  mode: MfaMode;
  isLoading: boolean;
  onVerified: (user: User) => void;
  onCancel?: () => void;
}

const CODE_LENGTH = 6;

const MfaFlow: React.FC<MfaFlowProps> = ({ challenge, mode, isLoading, onVerified, onCancel }) => {
  const { t } = useTranslation('auth');
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [manualSecret, setManualSecret] = useState<string | null>(null);
  const [enrollError, setEnrollError] = useState<string | null>(null);

  // 'setup' mode: fetch the enrollment URI immediately and render the QR.
  useEffect(() => {
    if (mode !== 'setup') return;
    let cancelled = false;
    setLocalLoading(true);
    authAPI
      .enrollMfa(challenge)
      .then(({ otpauthUri, secret }) => {
        if (cancelled) return;
        setManualSecret(secret);
        return QRCode.toDataURL(otpauthUri, {
          width: 220,
          margin: 1,
          color: { dark: '#111827', light: '#ffffff' },
        }).then((url) => !cancelled && setQrDataUrl(url));
      })
      .catch((err) => {
        if (!cancelled) {
          setEnrollError(err?.message || t('mfa.enrollError'));
        }
      })
      .finally(() => !cancelled && setLocalLoading(false));
    return () => {
      cancelled = true;
    };
  }, [challenge, mode, t]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.replace(/\D/g, '');
    if (trimmed.length !== CODE_LENGTH) {
      setLocalError(t('mfa.errorCodeRequired', { count: CODE_LENGTH }));
      return;
    }
    setLocalError(null);
    setLocalLoading(true);
    try {
      const response =
        mode === 'setup'
          ? await authAPI.activateMfa(challenge, trimmed)
          : await authAPI.verifyMfa(challenge, trimmed);
      if (response.user) {
        onVerified(response.user);
      } else {
        setLocalError(t('mfa.errorUnexpected'));
      }
    } catch (err) {
      const message = (err as any)?.message || t('mfa.errorInvalidCode');
      setLocalError(message);
      setCode('');
    } finally {
      setLocalLoading(false);
    }
  };

  const busy = localLoading || isLoading;

  return (
    <div className="mt-6 space-y-5" aria-live="polite">
      {mode === 'setup' ? (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('mfa.setupTitle')}
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            {t('mfa.setupBody')}
          </p>

          {localLoading && <p className="mt-4 text-sm text-gray-500">{t('mfa.loadingConfig')}</p>}
          {enrollError && (
            <p className="mt-3 text-sm text-red-600" role="alert">{enrollError}</p>
          )}

          {qrDataUrl && (
            <div className="mt-4 flex flex-col items-center">
              <img
                src={qrDataUrl}
                alt={t('mfa.qrAlt')}
                className="rounded-lg border border-gray-200 bg-white p-2"
                width={220}
                height={220}
              />
              {manualSecret && (
                <details className="mt-3 text-left max-w-md">
                  <summary className="text-xs font-medium text-gray-600 cursor-pointer">
                    {t('mfa.manualConfig')}
                  </summary>
                  <code className="mt-1 block break-all rounded bg-gray-100 px-2 py-1 text-xs text-gray-800 select-all">
                    {manualSecret}
                  </code>
                </details>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('mfa.verifyTitle')}
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            {t('mfa.verifyBody')}
          </p>
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="mfa-code" className="sr-only">
            {t('mfa.codeLabel')}
          </label>
          <input
            id="mfa-code"
            name="mfa-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            pattern="[0-9]*"
            maxLength={CODE_LENGTH}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            disabled={busy || Boolean(enrollError)}
            className="text-center text-lg tracking-[0.5em] font-mono w-full rounded-md border border-gray-300 px-3 py-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60"
          />
        </div>

        {localError && (
          <p className="text-sm text-red-600" role="alert">{localError}</p>
        )}

        <button
          type="submit"
          disabled={
            busy ||
            code.replace(/\D/g, '').length !== CODE_LENGTH ||
            // setup mode: block until the enrollment QR actually loaded
            (mode === 'setup' && qrDataUrl === null)
          }
          className="w-full flex justify-center py-2.5 px-4 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? t('mfa.busy') : mode === 'setup' ? t('mfa.activate') : t('mfa.verify')}
        </button>

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="w-full text-sm text-gray-500 hover:text-gray-700"
          >
            {t('mfa.back')}
          </button>
        )}
      </form>
    </div>
  );
};

export default MfaFlow;
